/**
 * Antibot da tela de acesso.
 *
 * As fontes exigem proteção antibot no pedido de código, e o servidor recusa a
 * requisição sem um token válido (`turnstile_token`, obrigatório).
 *
 * Duas regras que este módulo materializa:
 *
 * 1. **A chave pública nunca é escrita no código.** Ela chega por variável de
 *    ambiente, `VITE_TURNSTILE_SITE_KEY`, e é a única das duas chaves do
 *    Turnstile que o navegador pode conhecer. A chave secreta vive só no
 *    servidor, em `TURNSTILE_SECRET_KEY`, e não tem nenhuma referência aqui.
 * 2. **Sem chave, falha honesta.** Não existe token de mentira, nem valor
 *    padrão que "destrave" o formulário. Mandar qualquer coisa faria o servidor
 *    recusar com erro de validação, escondendo a causa real atrás de uma
 *    mensagem enganosa.
 */

export interface Antibot {
  /** Carrega e desenha o desafio dentro do elemento informado. */
  montar(container: HTMLElement): Promise<void>;
  /** Descarta o desafio desenhado, se houver. */
  desmontar(): void;
  /** Token do desafio resolvido, ou `null` quando não há como obtê-lo. */
  obterToken(): Promise<string | null>;
  /** Renova o desafio depois que a requisição com o token termina. */
  renovar(): void;
  /** Motivo legível quando `obterToken` devolve `null`. */
  motivoIndisponivel(): string;
}

const URL_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const SEM_CHAVE =
  'A proteção antibot ainda não está configurada nesta instalação. ' +
  'Falta definir a chave pública do Turnstile.';

const NAO_RESOLVIDO = 'Conclua a verificação de segurança acima antes de pedir o código.';

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  getResponse(widgetId?: string): string | undefined;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

function chavePublica(): string | undefined {
  const valor = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  return valor !== undefined && valor.length > 0 ? valor : undefined;
}

function api(): TurnstileApi | undefined {
  return (globalThis as { turnstile?: TurnstileApi }).turnstile;
}

/**
 * Carrega o script uma única vez.
 *
 * O endereço `challenges.cloudflare.com` é o único hospedeiro externo de script
 * permitido pela política de segurança, e `npm run check:csp` reprova a build se
 * alguém acrescentar outro.
 */
let carregando: Promise<void> | null = null;

function carregarScript(): Promise<void> {
  if (api() !== undefined) {
    return Promise.resolve();
  }
  if (carregando !== null) {
    return carregando;
  }

  carregando = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = URL_SCRIPT;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      resolve();
    });
    script.addEventListener('error', () => {
      carregando = null;
      reject(new Error('script do antibot não carregou'));
    });
    document.head.appendChild(script);
  });

  return carregando;
}

let widgetId: string | null = null;
let tokenEmUso = false;

function descartar(turnstile: TurnstileApi): void {
  if (widgetId !== null) {
    turnstile.remove(widgetId);
    widgetId = null;
  }
  tokenEmUso = false;
}

export const turnstileAntibot: Antibot = {
  async montar(container: HTMLElement): Promise<void> {
    const chave = chavePublica();
    if (chave === undefined) {
      return;
    }

    try {
      await carregarScript();
    } catch {
      // A tela já trata a ausência de token; não há o que fazer aqui além de
      // não fingir que deu certo.
      return;
    }

    const turnstile = api();
    if (turnstile === undefined) {
      return;
    }

    // O desafio anterior precisa ser descartado antes de desenhar o novo. Sem
    // isto, o desenho ficaria preso ao primeiro recipiente e sumiria da tela em
    // duas situações reais: a montagem dupla que o React faz em
    // desenvolvimento, e a volta da participante à etapa de e-mail depois de
    // pedir um código. Nas duas, o recipiente vivo passa a ser outro elemento.
    descartar(turnstile);

    widgetId = turnstile.render(container, {
      sitekey: chave,
      size: 'compact',
      // Idioma fixo: a interface é em português, e deixar o widget seguir o
      // navegador criaria uma tela em dois idiomas.
      language: 'pt-br',
    });
  },

  desmontar(): void {
    const turnstile = api();
    if (turnstile !== undefined) {
      descartar(turnstile);
    } else {
      widgetId = null;
      tokenEmUso = false;
    }
  },

  obterToken(): Promise<string | null> {
    const turnstile = api();
    if (turnstile === undefined || widgetId === null || tokenEmUso) {
      return Promise.resolve(null);
    }
    const token = turnstile.getResponse(widgetId);
    if (token === undefined || token.length === 0) {
      return Promise.resolve(null);
    }
    tokenEmUso = true;
    return Promise.resolve(token);
  },

  renovar(): void {
    const turnstile = api();
    if (turnstile !== undefined && widgetId !== null) {
      turnstile.reset(widgetId);
    }
    tokenEmUso = false;
  },

  motivoIndisponivel(): string {
    return chavePublica() === undefined ? SEM_CHAVE : NAO_RESOLVIDO;
  },
};
